/**
 * §8's signed-out behaviour and §13.2, as a page: Subscriptions, your playlists and History are
 * **disabled with a sign-in affordance** rather than hidden, and never shown broken.
 *
 * Every entry has an `anonymous` line, and that is the point of the gate rather than decoration: a page
 * that only says what is missing reads as a wall. Saying what still works with no account at all is
 * what makes it a door.
 *
 * No `*emphasis*` anywhere in here — these are plain strings rendered as text, and a browser pass once
 * showed the asterisks on screen (docs/specs/testing.md).
 */
export const gated = {
  subscriptions: {
    title: 'Sign in to see your subscriptions',
    detail:
      'The channels you follow are stored in your account on the instance, not in Visious, so this page needs you signed in before it can ask for them.',
    anonymous:
      'Watching, searching, channels and public playlists all work without an account. This page is the exception, because the answer is specific to you.',
  },
  playlists: {
    title: 'Sign in to see your playlists',
    detail:
      'Your own and private playlists live in your account on the instance, and Visious deliberately keeps no copy of them here: one list that is sometimes yours and sometimes stale is the bug this avoids.',
    anonymous:
      'A public playlist link still opens without signing in. Signing in is only for the playlists that belong to you.',
  },
  history: {
    title: 'Sign in to see your history',
    detail:
      'Watch history is recorded on the instance, so it follows you between browsers and devices. It cannot be read until you sign in.',
    anonymous:
      'Nothing is recorded while you are signed out, so a signed-out session leaves no history behind either.',
  },
  channels: {
    title: 'Sign in to manage your subscriptions',
    detail:
      'The channels you follow are stored in your account on the instance, so adding and removing them needs you signed in.',
    anonymous:
      'Channels themselves still open without an account. Following them is what needs one.',
  },

  /**
   * A short second line about the session itself. Three of the five statuses have one: two where the
   * page is blocked and the session is the reason, and **`under-scoped`, which is not blocked at all** —
   * the gate lets it through, so without this line the page behind it would assert that everything is
   * fine while the request it needs is refused.
   */
  reason: {
    expired: 'Your session on this instance has expired, so signing in again is what this needs.',
    unreadable:
      'There is a session stored for this instance, but it is not one Visious can read. Signing in replaces it.',
    underScoped:
      'This session is missing some of the permissions Visious asks for, so this page may be refused. The sign-in page says which, and signing in again asks for all of them.',
  },

  checking: 'Checking this browser for a session…',
  openSignIn: 'Open the sign-in page',
  signIn: 'Sign in to your instance',
} as const;
